/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { Button } from 'components/_new/Button';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { TimelineEventItem } from 'types/Timeline';
import { Loader } from 'components/_new/Loader';
import { useMutation } from '@tanstack/react-query';
import { timelineApi } from 'api/timeline';
import { usePanel } from 'contexts/panelContext';
import { useTimeline } from './useTimeline';
import { TimelineEventCard } from './TimelineEventCard';
import { TimelineEventDetailsDialog } from './TimelineEventDetailsDialog';

type TimelineListProps = {
	dialogMode?: boolean;
};

export const TimelineList = ({ dialogMode = false }: TimelineListProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { hash } = useLocation();

	const { currentApplicationData } = usePanel();

	const [scopeEventId, setScopeEventId] = useState<number | null>(null);

	const normalizeEventId = (eventId: TimelineEventItem['eventId']) => (eventId === null ? null : Number(eventId));

	useEffect(() => {
		if (!dialogMode) {
			const hashEventId = hash.startsWith('#event-') ? parseInt(hash.substring('#event-'.length), 10) : NaN;
			if (!Number.isNaN(hashEventId)) {
				setScopeEventId(hashEventId);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hash, dialogMode]);

	const {
		grouppedData,
		data,
		fetchNextPage,
		fetchPreviousPage,
		isFetchingNextPage,
		isFetchingPreviousPage,
		hasNextPage,
		hasPreviousPage,
		loading,
		updateData,
	} = useTimeline(scopeEventId);

	const [selectedEvent, setSelectedEvent] = useState<TimelineEventItem | null>(null);

	useEffect(() => {
		if (scopeEventId && data?.length > 0) {
			const initialOpenTimelineEvent = data.find((item) => normalizeEventId(item.eventId) === scopeEventId);
			if (initialOpenTimelineEvent) {
				setSelectedEvent(initialOpenTimelineEvent);
				navigate({ hash: undefined }, { replace: true });
				setScopeEventId(null);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, scopeEventId]);

	const handleUpdate = (updatedItem: TimelineEventItem) => {
		updateData(updatedItem);
		if (selectedEvent?.key === updatedItem.key) {
			setSelectedEvent(updatedItem);
		}
	};

	const { mutate: registerVisitMutate } = useMutation((eventId: number) =>
		timelineApi.registerVisit(Number(currentApplicationData?.id), eventId)
	);

	const openEventDetails = (timelineEvent: TimelineEventItem) => {
		const normalizedEventId = normalizeEventId(timelineEvent.eventId);
		if (!selectedEvent && normalizedEventId) {
			registerVisitMutate(normalizedEventId);
			setSelectedEvent(timelineEvent);
		} else {
			setSelectedEvent(null);
		}
	};

	if (loading && !grouppedData.length) {
		return <Loader />;
	}

	return (
		<>
			<TimelineEventDetailsDialog
				data={selectedEvent}
				onUpdate={handleUpdate}
				visible={Boolean(selectedEvent)}
				onHide={() => {
					setSelectedEvent(null);
				}}
			/>
			<div className="flex flex-column gap-2 justify-content-center align-items-center">
				{grouppedData.length > 0 ? (
					<>
						{hasPreviousPage ? (
							<Button
								label={t('timeline.action.loadPrev')}
								variant="primary-outlined"
								onClick={() => fetchPreviousPage()}
								loading={isFetchingPreviousPage}
							/>
						) : (
							<div className="text-muted">{t('timeline.noPrevItems')}</div>
						)}

						{grouppedData.map(({ key, name, items }) => {
							return (
								<div key={key} className="w-full">
									<div className="text-primary text-lg my-2">{name}</div>
									<ul className="flex flex-column gap-2 list-none">
										{items.map((item) => {
											return (
												<TimelineEventCard
													key={item.key}
													data={item}
													onUpdate={handleUpdate}
													onTitleClick={(timelineEvent) => {
														openEventDetails(timelineEvent);
													}}
												/>
											);
										})}
									</ul>
								</div>
							);
						})}

						{hasNextPage ? (
							<Button
								label={t('timeline.action.loadNext')}
								variant="primary-outlined"
								onClick={() => fetchNextPage()}
								loading={isFetchingNextPage}
							/>
						) : (
							<div className="text-muted">{t('timeline.noNextItems')}</div>
						)}
					</>
				) : (
					<div>{t('misc.noResults')}</div>
				)}
			</div>
		</>
	);
};
